import React, { useEffect, useState, useCallback } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { searchStudentsByName } from "../../api/sponteAPI";

/**
 * Componente reutilizável para busca assíncrona de alunos
 * @param {Object} props
 * @param {Function} props.onSelect - Callback chamado ao selecionar um aluno (retorna objeto {AlunoID, Nome} ou null)
 * @param {string} props.label - Texto do label do input
 */
export const StudentSearch = ({ onSelect, label = "Digite o nome do aluno para consulta (mín. 3 caracteres)" }) => {
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [options, setOptions] = useState([]);

    // Debounce para busca
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (searchValue.length >= 3) {
                try {
                    setSearchLoading(true);
                    const results = await searchStudentsByName(searchValue);

                    if (results && Array.isArray(results)) {
                        // Formatar para o Autocomplete, mas mantendo o objeto original acessível
                        // O Autocomplete do MUI trabalha bem com objetos se configurado corretamente
                        // Mas para consistência com o código anterior, vamos criar uma estrutura que facilita
                        setOptions(results);
                    } else {
                        setOptions([]);
                    }
                } catch (error) {
                    console.error("Erro na busca de alunos:", error);
                    setOptions([]);
                } finally {
                    setSearchLoading(false);
                }
            } else {
                setOptions([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchValue]);

    const handleSearchChange = useCallback((event, newInputValue) => {
        setSearchValue(newInputValue);
    }, []);

    const handleSelect = useCallback(
        (event, newValue) => {
            onSelect(newValue);
        },
        [onSelect]
    );

    return (
        <Autocomplete
            id="student-async-search"
            disablePortal
            options={options}
            loading={searchLoading}
            getOptionLabel={(option) => `${option.AlunoID} - ${option.Nome}`}
            onInputChange={handleSearchChange}
            onChange={handleSelect}
            filterOptions={(x) => x} // Desabilita filtro client-side pois já filtramos no backend
            isOptionEqualToValue={(option, value) => option.AlunoID === value.AlunoID}
            sx={{ width: "100%" }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            noOptionsText={searchValue.length < 3 ? "Digite pelo menos 3 caracteres" : "Nenhum aluno encontrado"}
        />
    );
};
